/**
 * The one refusal this site does not treat as a failure.
 *
 * A guest arrives on a marketing offer's landing page, presses Rezerviraj, and the
 * link carries a signed context naming that offer (docs/MARKETING.md §10). The house
 * may have withdrawn it since: the campaign ended, the offer was taken off the site,
 * or its content was rewritten and the link now names a version that no longer exists.
 * The API then answers 409 with `{"error": {"code": 59}}`.
 *
 * That is not a broken booking engine and it is not the guest's mistake. The rooms are
 * still for sale — only the campaign price is gone — so the right answer is to drop
 * the offer, ask the same question again at the house's own prices, and SAY SO. A link
 * pinned to a post outlives the campaign it advertised, and a dead end there is a
 * guest lost to a promise the hotel made months ago.
 *
 * It reads the body the same two ways `libs/license.ts` does and for the same reason:
 * a `.json()` call hands `onFetchError` a parsed object, and a call that never asked
 * for JSON hands it text.
 */

/** The API's code for a marketing offer that no longer holds. */
const OFFER_NO_LONGER_VALID = 59

/**
 * The API's code for a package the house still sells and cannot deliver on these
 * nights: the garage is full, the sauna has no free hour (docs/MARKETING.md §12).
 *
 * IT MUST NOT BE TREATED LIKE THE ONE ABOVE. Dropping the offer and re-quoting at the
 * ordinary price would sell the guest a room while silently removing the parking space
 * they came for — the same class of defect §10 exists to close, one field along. So the
 * token is KEPT, the refusal is shown with the part's own name in it, and the guest is
 * asked for different dates.
 */
const OFFER_COMPONENT_SOLD_OUT = 70

/**
 * What `book()` answers when the offer was withdrawn at the last step.
 *
 * A status of its own rather than the 409 the API used, because the summary screen
 * already reads 409 as "the room has just been taken" — a different sentence and a
 * different next step. Well outside the HTTP range, so it can never collide with a
 * real one.
 */
export const OFFER_WITHDRAWN_STATUS = 1059

/** The parsed error body of a refusal, or null for a 409 that is not one of ours. */
const refusal = (status: number | undefined, body: unknown): { code?: number, message?: string } | null => {
    if (status !== 409) return null

    let parsed = body

    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed)
        } catch {
            // A 409 whose body is not JSON is an ordinary conflict, not one of these.
            return null
        }
    }

    return (parsed as { error?: { code?: number, message?: string } })?.error ?? null
}

/** Reads one refused answer and says whether the offer is what was refused. */
export const offerWithdrawn = (status: number | undefined, body: unknown): boolean =>
    refusal(status, body)?.code === OFFER_NO_LONGER_VALID

/**
 * The other offer refusal: a part of the package has run out for these dates.
 *
 * Answered with the SERVER's own sentence rather than a translated one, because the
 * only useful thing it carries is the name of the part that is gone — "Parkirno mesto"
 * — and this site has no way to know what a house called its car park. The screen wraps
 * it in a sentence of its own language and prints the name inside it.
 */
export const componentSoldOut = (status: number | undefined, body: unknown): string => {
    const error = refusal(status, body)

    if (error?.code !== OFFER_COMPONENT_SOLD_OUT) return ''

    // A message is always sent, but a sentinel with no sentence would leave the screen
    // silent — so an empty one still counts as sold out and the caller prints its own
    // general wording.
    return error.message || ' '
}
